"use client";

import { Activity, ArrowRight, Baby, HeartPulse, Microscope, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  icon: Stethoscope,
  title: "Aerospace Components",
  description: "Precision anodized aluminum parts engineered for high-stress environments, ensuring durability and weight efficiency in flight systems."
}, {
  icon: Baby,
  title: "Medical Devices",
  description: "Biocompatible anodized finishes for surgical instruments and diagnostic equipment, providing superior corrosion resistance and sterilization stability."
}, {
  icon: HeartPulse,
  title: "Architectural Panels",
  description: "High-performance anodized aluminum for exterior facades and interior design, offering long-lasting color consistency and weather protection."
}, {
  icon: Activity,
  title: "Automotive Parts",
  description: "Hard-coat anodizing for engine components and trim, delivering exceptional wear resistance and a premium aesthetic finish."
}, {
  icon: ShieldCheck,
  title: "Consumer Electronics",
  description: "Precision-finished aluminum housings for high-end electronics, combining sleek visual appeal with robust protection against daily wear."
}, {
  icon: Microscope,
  title: "Industrial Machinery",
  description: "Heavy-duty anodized surfaces for manufacturing equipment, designed to withstand harsh chemical exposure and mechanical friction."
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = service.icon;
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 80}ms`
  }} className={`group relative flex flex-col rounded-lg border border-border bg-card p-8 sm:p-10 transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-lg ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="mb-8">
        <div className="flex h-[88px] w-[88px] items-center justify-center rounded-lg bg-secondary/20 transition-colors duration-500 group-hover:bg-secondary">
          <Icon className="h-[44px] w-[44px] text-primary transition-colors duration-500 group-hover:text-accent" strokeWidth={1.25} />
        </div>
      </div>

      <h3 className="font-default text-2xl font-medium text-card-foreground mb-3">
        {service.title}
      </h3>

      <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8 flex-1">
        {service.description}
      </p>

      
    </div>;
}
export default function WhereAnodizedAluminumIsUsed() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="668" id="where-anodized-aluminum-is-used" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20 lg:mb-24 transition-all duration-700 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-6 flex flex-col">
            <span className="font-default text-xs tracking-[0.2em] uppercase text-primary mb-6">
              Industrial Applications
            </span>
            <h2 className="font-default text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] font-medium text-card-foreground tracking-tight">
              Precision anodizing for every industry.
            </h2>
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-8 max-w-xl">
              From aerospace engineering to consumer electronics, our anodizing solutions provide the essential protection and aesthetic finish required for high-performance components.
            </p>
          </div>

          <div className="lg:col-span-6 lg:pt-24">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              At our facility, we prioritize technical excellence and material integrity. We work closely with engineers and manufacturers to ensure every batch meets rigorous industry standards for corrosion resistance, hardness, and color uniformity.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <ServiceCard key={service.title} service={service} index={i} />)}
        </div>

        {}
        
      </div>
    </section>;
}